import { Input, Component, Output, EventEmitter, HostListener, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Utilities } from '@common/utils';
import { FeedbackError, FeedbackMessage, FeedbackRating } from '../../interfaces/feedback-request.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  standalone: false,
  selector: 'd3-feedback-dialog',
  templateUrl: './feedback-dialog.component.html',
  styleUrls: ['./feedback-dialog.component.scss'],
})
export class D3FeedbackDialogComponent implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  safeIntroMessage: SafeHtml;
  safeThankYouMessage: SafeHtml;
  safeFeedbackErrorMessage: SafeHtml;
  safeFooterMessage: SafeHtml;
  configurationError = 'Feedback component not configured correctly. Missing required input messages (intro/thankYou).';

  ngOnInit(): void {
    this.checkScreenWidth();

    if (this.autoOpenMs !== null && this.autoOpenMs >= 0) {
      setTimeout(() => {
        this.openModal('information');
        this.cdr.detectChanges();
      }, this.autoOpenMs);
    }

    if (!this.introMessage && this.initialStep === 1) {
      //if initial step is intro window and there's no intro message, throw configuration error
      this.disableFeedback = true;
      console.log(this.configurationError);
    } else {
      // Sanitize the HTML content
      this.safeIntroMessage = this.sanitizer.bypassSecurityTrustHtml(this.introMessage.feedbackMessage);
      if (this.footerMessage?.feedbackMessage) {
        this.safeFooterMessage = this.sanitizer.bypassSecurityTrustHtml(this.footerMessage.feedbackMessage);
      }
    }
  }

  @Input() uniqueId = 'id';
  // @Input() calloutVariant: 'information' | 'important' | 'success' | 'emergency' | 'event';
  @Input() buttonText;
  @Input() backButtonText;
  @Input() backButtonType = 'secondary';
  @Input() linkText;
  @Input() buttonType = 'primary';
  @Input() buttonVariant = null;
  @Input() buttonDisabled = false;
  @Input() calloutVariant: string = null;
  @Input() headerText;
  @Input() cancelText = 'Cancel';
  @Input() cancelButtonType = 'secondary'; //allow override of button type for cancel. sometimes it'll be secondary or tertiary
  @Input() submitText = 'Submit';
  @Input() startText = 'Start';
  @Input() closeText = 'Close';
  @Input() backText = 'Back';
  @Input() displayContent = false;
  @Input() initialStep = 1;
  @Input() autoOpenMs = -1;
  @Input() maxWidth = '644px';
  @Input() introMessage: FeedbackMessage;
  @Input() footerMessage: FeedbackMessage;
  @Output() outputAction = new EventEmitter();

  feedbackError: FeedbackError = null;
  thankYouMessage: FeedbackMessage = null;
  ratings = FeedbackRating;

  isMobile = false;
  isOpen = false;
  mobileBreakpointWidth = 780;
  currentStep = 1;
  textAreaMaxLength = 300;
  disableFeedback = false;

  textAreaHelperText = 'Do not include personal information like SIN, passwords, addresses, etc.';
  formGroup = new UntypedFormGroup({
    rating: new FormControl(null, Validators.required),
    comment: new FormControl('', [Validators.maxLength(this.textAreaMaxLength)]),
    hasTechnicalIssues: new FormControl('', Validators.required),
    technicalIssue: new FormControl('', [Validators.required, Validators.maxLength(this.textAreaMaxLength)]),
  });

  issuesChanged() {
    if (this.formGroup.controls.hasTechnicalIssues.value === 'yes') {
      this.formGroup.controls.technicalIssue.setValidators([
        Validators.required,
        Validators.maxLength(this.textAreaMaxLength),
      ]);
    } else {
      this.formGroup.controls.technicalIssue.clearValidators();
    }
    this.formGroup.controls.technicalIssue.updateValueAndValidity();
  }

  openModal(calloutVariant: string, event: Event = null) {
    if (this.disableFeedback) {
      throw new Error(this.configurationError);
    }
    this.currentStep = 1;

    //if this form is initialized on a different step, load that step after DOM
    if (this.currentStep !== this.initialStep) {
      setTimeout(() => {
        this.currentStep = this.initialStep;
      }, 0);
    }

    this.calloutVariant = calloutVariant;
    this.isOpen = true;

    if (event) {
      event.preventDefault();
    }
    this.cdr.markForCheck(); // Mark the component for change detection
  }

  closeModal() {
    this.resetForm();
    this.isOpen = false;
    this.cdr.markForCheck(); // Mark the component for change detection
  }

  backFeedback() {
    this.closeModal();
    const backUrl = this.route.snapshot.queryParamMap.get('backUrl');
    if (backUrl) {
      this.router.navigateByUrl(backUrl);
    }
  }

  startFeedback() {
    this.currentStep = 2;
  }

  submitFeedback() {
    const technicalIssuesTouchedState = this.formGroup.controls.technicalIssue.touched;
    this.formGroup.markAllAsTouched();
    const formValue = this.formGroup.value;

    if (!formValue.hasTechnicalIssues || (formValue.hasTechnicalIssues === 'no' && !technicalIssuesTouchedState)) {
      //if technicalissues text area isn't visible, reset it's state to what it was previously
      this.formGroup.controls.technicalIssue.markAsUntouched();
    }

    if (this.formGroup.valid) {
      // this.currentStep = 3;
      if (!formValue.hasTechnicalIssues || formValue.hasTechnicalIssues === 'no') {
        formValue.technicalIssue = null;
      }
      const feedbackRequest = formValue;
      feedbackRequest.rating = Number(feedbackRequest.rating);
      this.outputAction.emit(feedbackRequest);
    }
  }

  resetForm() {
    this.currentStep = 1;
    this.formGroup.controls.rating.setValue(null);
    this.formGroup.controls.comment.setValue(null);
    this.formGroup.controls.hasTechnicalIssues.setValue(null);
    this.formGroup.controls.technicalIssue.setValue(null);
    this.formGroup.markAsUntouched();
  }

  //attribute variant cannot be empty string otherwise it throws a console error
  get getButtonVariant() {
    if (Utilities.hasValue(this.buttonVariant)) {
      return this.buttonVariant;
    }
    return null;
  }

  get getCalloutVariant() {
    if (Utilities.hasValue(this.calloutVariant)) {
      return this.calloutVariant;
    }
    return null;
  }

  errorMessage(field: string) {
    const control = this.formGroup.controls[field] as FormControl;
    let error = '';
    if (control?.touched) {
      if (control.errors?.required) {
        error = 'This field is required';
      } else if (control.errors?.maxlength) {
        error = `Cannot exceed maximum character length of ${this.textAreaMaxLength}`;
      }
    }
    return error;
  }

  // getMaxWidth() {
  //   let maxWidth = '800px';
  //   if (this.currentStep === 1) {
  //     maxWidth = '400px';
  //   } else if (this.currentStep === 2) {
  //     maxWidth = '800px';
  //   } else if (this.currentStep === 3) {
  //     maxWidth = '400px';
  //   }
  //   return maxWidth;
  // }

  @HostListener('window:resize', [])
  onResize(): void {
    this.checkScreenWidth();
  }

  private checkScreenWidth(): void {
    this.isMobile = window.innerWidth < this.mobileBreakpointWidth;
  }

  gotoStep(step: number) {
    this.currentStep = step;
  }

  //this is used by the consuming component to show error if their save implementation fails.
  //this allows calling component to specify different messages for different errors
  public displayFeedbackError(value: FeedbackError) {
    this.feedbackError = value;
    if (value) {
      this.safeFeedbackErrorMessage = this.sanitizer.bypassSecurityTrustHtml(this.feedbackError.errorMessage);
      this.currentStep = 4;
    }
  }

  public displayFeedbackThankYou(value: FeedbackMessage) {
    this.thankYouMessage = value;
    this.safeThankYouMessage = this.sanitizer.bypassSecurityTrustHtml(this.thankYouMessage.feedbackMessage);
    this.currentStep = 3;
  }

  get ratingsList() {
    const returnRatings = [...this.ratings];

    if (this.isMobile) {
      return returnRatings.reverse();
    }
    return returnRatings;
  }
}
